import React, { FC, ReactElement, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import i18next from 'i18next';

type AvatarCropperProps = {
  srcImage: string | null;
  overlayOpacity?: number;
  ringWidth?: number;
  onPreviewChange?: (dataUrl: string) => void;
}

type Position = { x: number; y: number };

const AvatarCropper: FC<AvatarCropperProps> = ({
  srcImage,
  overlayOpacity = 0.35,
  ringWidth = 2,
  onPreviewChange,
}: AvatarCropperProps): ReactElement => {
  const [diameter, setDiameter] = useState<number>(320);

  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [naturalSize, setNaturalSize] = useState<{ width: number; height: number } | null>(null);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const draggingRef = useRef(false);
  const dragStartRef = useRef<Position>({ x: 0, y: 0 });
  const startPositionRef = useRef<Position>({ x: 0, y: 0 });

  useEffect(() => {
    const element = containerRef.current;
    if (!element) {
      return;
    }

    const observer: ResizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]): void => {
      const contentWidth: number = entries[0].contentRect.width;
      const diameter: number = Math.max(260, Math.floor(contentWidth));
      setDiameter(diameter);
    });
    observer.observe(element);
    return (): void => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    if (!srcImage) {
      setNaturalSize(null);
      return;
    }

    const image: HTMLImageElement = new Image();
    image.decoding = 'async';
    image.onload = (): void => {
      setNaturalSize({ width: image.naturalWidth, height: image.naturalHeight });
      setPosition({ x: 0, y: 0 });
    };
    image.onerror = (): void => {
      setNaturalSize(null);
    };
    image.src = srcImage;
  }, [srcImage]);

  const minScale = useMemo(() => {
    if (!naturalSize) {
      return 1;
    }

    return Math.min(diameter / naturalSize.width, diameter / naturalSize.height);
  }, [naturalSize, diameter]);

  useEffect(() => {
    if (naturalSize) {
      setScale(minScale);
    }
  }, [naturalSize, minScale]);

  const beginDrag = useCallback((clientX: number, clientY: number) => {
    draggingRef.current = true;
    dragStartRef.current = { x: clientX, y: clientY };
    startPositionRef.current = { ...position };
  }, [position]);

  const onMouseDown = (event: React.MouseEvent<HTMLDivElement>): void => {
    event.preventDefault();
    beginDrag(event.clientX, event.clientY);
  };

  const onMouseMove = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (!draggingRef.current) {
      return;
    }

    const dx: number = event.clientX - dragStartRef.current.x;
    const dy: number = event.clientY - dragStartRef.current.y;
    setPosition({ x: startPositionRef.current.x + dx, y: startPositionRef.current.y + dy });
  };

  const onMouseUp = (): void => { draggingRef.current = false; };
  const onMouseLeave = (): void => { draggingRef.current = false; };

  const onTouchStart = (event: React.TouchEvent<HTMLDivElement>): void => {
    if (event.touches.length !== 1) {
      return;
    }
    const touch: React.Touch = event.touches[0];
    beginDrag(touch.clientX, touch.clientY);
  };

  const onTouchMove = (event: React.TouchEvent<HTMLDivElement>): void => {
    if (!draggingRef.current || event.touches.length !== 1) {
      return;
    }

    const touch: React.Touch = event.touches[0];
    const dx: number = touch.clientX - dragStartRef.current.x;
    const dy: number = touch.clientY - dragStartRef.current.y;
    setPosition({ x: startPositionRef.current.x + dx, y: startPositionRef.current.y + dy });
  };

  const onTouchEnd = (): void => { draggingRef.current = false; };

  const onWheel = (event: React.WheelEvent<HTMLDivElement>): void => {
    event.preventDefault();
    const delta: number = -Math.sign(event.deltaY) * 0.05;
    setScale((previous: number) => {
      const next: number = previous + delta;
      return Math.max(minScale, Math.min(next, minScale * 4));
    });
  };
  
  const renderPreview = useCallback((forPreview: boolean): string | null => {
    if (!srcImage || !naturalSize || diameter <= 0) {
      return null;
    }

    const displayedWidth: number = naturalSize.width * scale;
    const displayedHeight: number = naturalSize.height * scale;

    const frameWidth: number = diameter;
    const frameHeight: number = diameter;

    const frameCenterX: number = frameWidth / 2 + position.x;
    const frameCenterY: number = frameHeight / 2 + position.y;

    const imgLeft: number = frameCenterX - displayedWidth / 2;
    const imgTop: number = frameCenterY - displayedHeight / 2;

    const cropXNative: number = (0 - imgLeft) / scale;
    const cropYNative: number = (0 - imgTop) / scale;
    const cropWidthNative: number = frameWidth / scale;
    const cropHeightNative: number = frameHeight / scale;

    const canvas: HTMLCanvasElement = document.createElement('canvas');
    canvas.width = frameWidth;
    canvas.height = frameHeight;

    const context: CanvasRenderingContext2D | null = canvas.getContext('2d');
    if (!context) {
      return null;
    }

    const image: HTMLImageElement = new Image();
    image.crossOrigin = 'anonymous';
    image.src = srcImage;

    const draw = (): string | null => {
      context.clearRect(0 ,0, frameWidth, frameHeight);
      context.save();

      context.beginPath();
      context.arc(frameWidth / 2, frameHeight / 2, frameWidth / 2, 0, Math.PI * 2);
      context.closePath();
      context.clip();

      context.drawImage(image, cropXNative, cropYNative, cropWidthNative,cropHeightNative, 0, 0, frameWidth, frameHeight);
      context.restore();

      return canvas.toDataURL('image/png');
    };

    if (forPreview) {
      if (image.complete && naturalSize.width > 0 && naturalSize.height > 0) {
        return draw();
      }
      image.onload = (): void => {
        const url: string | null = draw();
        if (url && onPreviewChange) {
          onPreviewChange(url);
        }
      };
      return null;
    }

    image.onload = (): void => {
      draw();
    };

    return null;
  }, [srcImage, naturalSize, diameter, position, scale, onPreviewChange]);

  useEffect(() => {
    const url: string | null = renderPreview(true);
    if (url && onPreviewChange) {
      onPreviewChange(url);
    }
  }, [renderPreview, onPreviewChange]);

  const radius: number = Math.floor(diameter / 2);

  if (!srcImage || !naturalSize) {
    return (
      <div ref={containerRef} className="w-100">
        <div
          className="text-muted text-center d-flex align-items-center justify-content-center"
          style={{ width: '100%', aspectRatio: '1 / 1', background: '#F8F9FA' }}
        >
          {i18next.t('avatarCropper.noImageSelected')}
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="w-100">
      <div
        style={{
          width: '100%',
          aspectRatio: '1 / 1',
          position: 'relative',
          background: '#F0F0F0',
          cursor: draggingRef.current ? 'grabbing' : 'grab',
          touchAction: 'none',
          userSelect: 'none',
        }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseLeave}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onWheel={onWheel}
      >
        <div
          style={{
            position: 'absolute',
            inset: 16,
            borderRadius: '50%',
            overflow: 'hidden',
          }}
        >
          <img
            src={srcImage}
            alt=""
            style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: `translate(-50%, -50%) translate(${position.x}px, ${position.y}px) scale(${scale})`,
              transformOrigin: 'center center',
              maxWidth: 'none',
              maxHeight: 'none',
              userSelect: 'none',
              pointerEvents: 'none',
            }}
            draggable={false}
          />

          <div
            style={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              borderRadius: '50%',
              boxShadow: `inset 0 0 0 ${ringWidth}px rgba(255, 255, 255, 0.9)`
            }}
          />
        </div>

        <div
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            background: `
             radial-gradient(
                circle at 50% 50%, 
                rgba(0, 0, 0, 0) ${radius - ringWidth}px, 
                rgba(0, 0, 0, ${overlayOpacity}) ${radius}px)`,
          }}
        />

      </div>

      <Form.Group className="w-100 mt-2">
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted">
            <i className="fa fa-magnifying-glass-minus" />
          </span>

          <Form.Range
            min={minScale}
            max={minScale * 4}
            step={0.01}
            value={scale}
            onChange={(event) => setScale(Number(event.target.value))}
          />

          <span className="text-muted">
            <i className="fa fa-magnifying-glass-plus" />
          </span>
        </div>

      </Form.Group>
    </div>
  );
};

export default AvatarCropper;
